import { Fragment, useEffect, useState } from "react";
import {
  ArrowLeft,
  CircleMinus,
  CirclePlus,
  Download,
  Edit,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { courseService } from "@/services/course";
import {
  analysisTermsType,
  animationSubtitle,
  annotationTermsType,
  articleQuestionOptionType,
  articleQuestionsType,
  audioSubtitle,
  chooseQuestionType,
  CourseDetailPage,
  CourseDetailType,
  grammarOptionType,
  grammarQuestionType,
  saveCourseParams,
} from "@/types/course.types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@radix-ui/react-tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OSSUpload } from "@/components/Upload";
import { Textarea } from "@/components/ui/textarea";
import MarkdownEditor, { MarkdownPreview } from "@/components/markdown-editor";
import downloadJson from "@/utils/downloadJson";

type textEditParamsType = {
  dialog: {
    title: string;
    type?: string;
    radioOption?: "option" | "key";
    radioOptionContent?: "title" | "value";
    rows?: number;
    question?: string;
    width?: string;
  };
  params: {
    article_id: string;
    cate: CourseDetailPage;
    field: string;
    sub_id?: string;
    value: string | string[];
    option_field?: string; // 选项修改字段
    option_id?: string; // 选项ID
    question_field?: string; // 题目修改字段
    question_id?: string; // 题目ID
    operation?: "add" | "delete";
  };
};
type mediaUpdateParamsType = {
  dialog: {
    title: string;
    accept: string;
    folder: string;
    fileNamePrefix: string;
  };
  params: {
    article_id: string;
    cate: CourseDetailPage;
    field: string;
    sub_id?: string;
    value: string;
    option_field?: string; // 选项修改字段
    option_id?: string; // 选项ID
    question_field?: string; // 题目修改字段
    question_id?: string; // 题目ID
  };
};

// 测试环境文件地址
// const matchFolder = {
//   cover: "liuliu_test/Cover_pics/", // 课程图片
//   titleMp3: "liuliu_test/assets/title_mp3/", // 课程标题音频
//   video: "liuliu_test/assets/video/", // 看动画-动画文件
//   articleMp3: "liuliu_test/content_Mp3/", // 读文章音频
//   articleTestMp3: "liuliu_test/assets/reading_comprehension_mp3/", // 阅读题音频
//   speakTestTitleMp3: "liuliu_test/assets/oral_question_mp3/", // 练口语题干音频
//   speakTestAnswerMp3: "liuliu_test/assets/oral_answer_mp3/", // 练口语答案音频
//   speakTestPic: "liuliu_test/oral_pics/", // 练口语图片
// };

const matchFolder = {
  cover: "Cover_pics/", // 课程图片
  titleMp3: "assets/title_mp3/", // 课程标题音频
  video: "assets/video/", // 看动画-动画文件
  articleMp3: "content_Mp3/", // 读文章音频
  articleTestMp3: "assets/reading_comprehension_mp3/", // 阅读题音频
  speakTestTitleMp3: "assets/oral_question_mp3/", // 练口语题干音频
  speakTestAnswerMp3: "assets/oral_answer_mp3/", // 练口语答案音频
  speakTestPic: "oral_pics/", // 练口语图片
};

const matchFileNamePrefix = {
  cover: "", // 课程图片
  titleMp3: "title_", // 课程标题音频
  video: "", // 看动画-动画文件
  articleMp3: "", // 读文章音频
  articleTestMp3: "", // 阅读题音频
  speakTestTitleMp3: "", // 练口语题干音频
  speakTestAnswerMp3: "", // 练口语答案音频
  speakTestPic: "", // 练口语图片
};
export default function CourseDetail() {
  // 路由信息-返回使用
  const navigate = useNavigate();
  // query信息
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [page, setPage] = useState<CourseDetailPage | "article">("base");
  const [innerPage, setInnerPage] = useState<CourseDetailPage>("audio");

  // 课程信息
  const [courseInfo, setCourseInfo] = useState<CourseDetailType>();
  // 修改的弹窗
  const [titleDialogShow, setTitleDialogShow] = useState<boolean>(false);
  const [subtitleDialogShow, setSubtitleDialogShow] = useState<boolean>(false);
  const [analysisTermsDialogShow, setAnalysisTermsDialogShow] =
    useState<boolean>(false);
  const [annotationTermsDialogShow, setAnnotationTermsDialogShow] =
    useState<boolean>(false);
  // 文本弹窗
  const [textEditDialogShow, setTextEditDialogShow] = useState<boolean>(false);
  const [textEditParams, setTextEditParams] = useState<textEditParamsType>();
  const [textEditValue, setTextEditValue] = useState<string>();

  // 多媒体弹窗
  const [mediaDialogShow, setMediaDialogShow] = useState<boolean>(false);
  const [mediaUpdateParams, setMediaUpdateParams] =
    useState<mediaUpdateParamsType>();
  const [mediaEditValue, setMediaEditValue] = useState<string>();

  // 添加选项
  const [addOption, setAddOption] = useState<string>("");
  const [addOptionTitle, setAddOptionTitle] = useState<string>("");
  const [addOptionDialogShow, setAddOptionDialogShow] =
    useState<boolean>(false);

  // 更改答案
  const [answerCheckbox, setAnswerCheckbox] = useState<string[]>();

  // 选中的解析词
  const [chosenAnalysisTerms, setChosenAnalysisTerms] =
    useState<analysisTermsType>();
  // 选中的注释词
  const [chosenAnnotationTerms, setChosenAnnotationTerms] =
    useState<annotationTermsType>();
  // 选中的题目
  const [chosenQuestion, setChosenQuestion] = useState<
    chooseQuestionType | grammarQuestionType | articleQuestionsType
  >();

  // 课程标题
  const [titleEdit, setTitleEdit] = useState<string>();
  // 字幕文件
  const [subtitleFile, setSubtitleFile] = useState<string>();
  // 修改的解析词
  const [analysisTerms, setAnalysisTerms] = useState<string>();
  // 修改的注释词
  const [annotationTerms, setAnnotationTerms] = useState<string>();

  // 返回上一页面
  const goBack = () => {
    navigate(-1);
  };

  // 获取课程详情
  const getCourseInfo = async () => {
    if (!id) {
      toast.error("url参数缺失", {
        description: "url课程id缺失",
      });
      return;
    }
    try {
      let cate = page;
      if (page == "article") {
        cate = innerPage;
      }
      const res = await courseService.getCourseDetail(id, cate);
      setCourseInfo(res);
      console.log("课程详情:", res);
    } catch (e) {
      toast.error("获取课程信息失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    }
  };
  // 格式化封面URL (确保完整路径)
  const formatCoverUrl = (
    url: string,
    type:
      | "cover"
      | "titleMp3"
      | "video"
      | "articleMp3"
      | "articleTestMp3"
      | "speakTestTitleMp3"
      | "speakTestAnswerMp3"
      | "speakTestPic"
  ) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // 假设有一个基础URL前缀
    return `${import.meta.env.VITE_OSS_HOST}${matchFolder[type]}${url}`;
  };
  const updateCourseInfo = async (params: saveCourseParams) => {
    try {
      await courseService.saveCourse(params);
      await getCourseInfo();
      toast.success("更新课程信息成功");
    } catch (e) {
      toast.error("更新课程信息失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    }
  };

  // 更新课程标题
  const updateTitle = async () => {
    // title更新前后对比，如有更新，调用更改课程标题接口 & 更新courseInfo中的标题信息
    if (!courseInfo) return;
    if (!titleEdit) return;
    await updateCourseInfo({
      article_id: courseInfo.articleId, // 课程ID
      cate: "base", // 变更模块
      field: "title", // 修改字段
      value: titleEdit,
    });
    setTitleDialogShow(false);
  };
  // 更新字幕文件
  const updateSubtitle = async () => {
    if (!subtitleFile) return;
    if (!courseInfo) return;
    await updateCourseInfo({
      article_id: courseInfo?.articleId,
      cate: page == "animation" ? "animation" : "audio",
      field: "subtitle",
      value: JSON.stringify(subtitleFile),
    });
    setSubtitleDialogShow(false);
  };
  // 下载字幕文件
  const downloadSubtitle = (
    data: { [key: string]: unknown }[],
    fileName: string
  ) => {
    downloadJson(data, fileName);
  };
  // 下载多媒体文件
  const downloadMedia = async (url: string) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      if (response.ok) {
        // 创建 a 标签
        const link = document.createElement("a");
        // 设置音频地址
        link.href = url;
        // 设置下载文件名（可选）
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).slice(2, 12);
        link.download = `${courseInfo?.articleId}-${timestamp}-${randomString}`;
        // 模拟点击
        document.body.appendChild(link);
        link.click();
        // 清理
        document.body.removeChild(link);
      } else {
        toast.error("找不到音频地址");
      }
    } catch (err) {
      console.error(err);
    }
  };
  // 更新解析词内容
  const updateAnalysisTerms = async () => {
    if (!courseInfo) return;
    if (!analysisTerms) return;
    if (!chosenAnalysisTerms) return;
    await updateCourseInfo({
      article_id: courseInfo.articleId, // 课程ID
      cate: "analysis", // 变更模块
      field: "explanation", // 修改字段
      value: analysisTerms,
      sub_id: chosenAnalysisTerms.id,
    });
    setAnalysisTermsDialogShow(false);
  };
  // 更新注释词内容
  const updateAnnotationTerms = async () => {
    if (!courseInfo) return;
    if (!annotationTerms) return;
    if (!chosenAnnotationTerms) return;
    await updateCourseInfo({
      article_id: courseInfo.articleId, // 课程ID
      cate: "annotation", // 变更模块
      field: "definition_cn", // 修改字段
      value: annotationTerms,
      sub_id: chosenAnnotationTerms.id,
    });
    setAnnotationTermsDialogShow(false);
  };

  // 更新文本弹窗
  const updateTextDialog = async () => {
    if (!textEditParams?.params) return;
    if (!textEditValue) return;
    await updateCourseInfo({
      ...textEditParams?.params,
      value: textEditValue,
    });
    setTextEditDialogShow(false);
  };
  // 更新多媒体弹窗
  const updateMediaDialog = async () => {
    if (!mediaUpdateParams?.params) return;
    if (!mediaEditValue) return;
    await updateCourseInfo({
      ...mediaUpdateParams?.params,
      value: mediaEditValue,
    });
    setMediaDialogShow(false);
  };
  // 更新问题答案
  const updateCheckboxQuestionAnswer = async () => {
    if (!answerCheckbox) return;
    if (!textEditParams?.params) return;
    await updateCourseInfo({
      ...textEditParams?.params,
      value: answerCheckbox,
    });
    setTextEditDialogShow(false);
  };

  // 添加问题选项
  const addNewOption = async () => {
    if (!courseInfo || !chosenQuestion || !addOption || !addOptionTitle) return;
    await updateCourseInfo({
      article_id: courseInfo.articleId, // 课程ID
      cate: "skills", // 变更模块
      sub_id: (chosenQuestion as chooseQuestionType)?.id,
      field: "question", // 修改字段
      question_field: "options",
      operation: "add", // add or delete 只有新增和删除选项才传，其他情况不传或者为空，否则会被误操作
      value: JSON.stringify({
        title: addOptionTitle,
        answer: "False",
        option: addOption,
      }),
    });
    setAddOptionDialogShow(false);
  };

  // 触发获取课程详情
  useEffect(() => {
    getCourseInfo();
  }, [page, innerPage]);

  return (
    <div className="space-y-4 h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">
          <ArrowLeft
            className="inline-block cursor-pointer mr-4"
            onClick={goBack}
          />
          课程详情
        </h2>
      </div>
      <div className="flex flex-row gap-5">
        <div>
          <Label className="inline-block text-lg font-semibold">课程id:</Label>{" "}
          {courseInfo?.articleId}
        </div>
        <div>
          <Label className="text-gray-400">蓝思值:</Label>{" "}
          {courseInfo?.blueScoreDesc}
        </div>
        <div>
          <Label className="text-gray-400">等级:</Label> {courseInfo?.level}
        </div>
        <div>
          <Label className="text-gray-400">学新词:</Label>{" "}
          {courseInfo?.wordCount}
        </div>
        <div>
          <Label className="text-gray-400">原序号:</Label> {id}
        </div>
      </div>
      <Tabs
        defaultValue="base"
        className="w-full mx-auto grow"
        onValueChange={(page: string) =>
          setPage(page as CourseDetailPage | "article")
        }
      >
        <TabsList>
          <TabsTrigger
            value="base"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            基本信息
          </TabsTrigger>
          <TabsTrigger
            value="core"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            学新词
          </TabsTrigger>
          <TabsTrigger
            value="animation"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            看动画
          </TabsTrigger>
          <TabsTrigger
            value="article"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            读文章
          </TabsTrigger>
          <TabsTrigger
            value="skills"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            拓技巧
          </TabsTrigger>
          <TabsTrigger
            value="grammerAndTest"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            学语法
          </TabsTrigger>
          <TabsTrigger
            value="speaking"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
          >
            练口语
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="base"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <div className="rounded-lg border p-4 space-y-4">
            <div className="flex flex-col gap-10">
              <div className="flex items-center">
                <Label className="inline-block w-36 text-lg font-semibold">
                  课程图片
                </Label>
                <img
                  src={
                    courseInfo?.picture &&
                    formatCoverUrl(courseInfo?.picture, "cover")
                  }
                  alt={courseInfo?.title}
                  className="w-full h-auto object-cover cursor-pointer"
                  onLoad={(e) => {
                    (e.target as HTMLElement).classList.remove("w-full");
                    (e.target as HTMLElement).classList.add("w-36");
                  }}
                />
                <Button
                  className="ml-2"
                  onClick={() => {
                    setMediaDialogShow(true);
                    setMediaUpdateParams({
                      dialog: {
                        title: "更改课程图片",
                        accept: "image/*",
                        folder: matchFolder["cover"],
                        fileNamePrefix:
                          matchFileNamePrefix["cover"] +
                          (courseInfo as CourseDetailType)?.articleId,
                      },
                      params: {
                        article_id: (courseInfo as CourseDetailType)?.articleId,
                        cate: "base", // 变更模块
                        field: "picture", // 修改字段
                        value: courseInfo?.picture as string,
                      },
                    });
                    setMediaEditValue(courseInfo?.picture);
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Edit className="h-4 w-4 text-indigo-600" />
                </Button>
              </div>
              <div className="flex items-center">
                <Label className="inline-block w-36 text-lg font-semibold text-">
                  课程标题
                </Label>
                <div>{courseInfo?.title}</div>
                <Button
                  className="ml-4"
                  onClick={() => setTitleDialogShow(true)}
                  variant="ghost"
                  size="icon"
                >
                  <Edit className="h-4 w-4 text-indigo-600" />
                </Button>
              </div>
              <div className="flex items-center">
                <Label className="inline-block w-36 text-lg font-semibold">
                  课程标题音频
                </Label>
                <a href="#" className="text-blue-600">
                  {courseInfo?.titleMp3}
                </a>
                <Button
                  className="ml-4"
                  onClick={() => {
                    if (courseInfo?.titleMp3) {
                      downloadMedia(
                        formatCoverUrl(courseInfo?.titleMp3, "titleMp3")
                      );
                    }
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Download className=" h-4 w-4 text-indigo-600 text-red-400" />
                </Button>
                <Button
                  className="ml-2"
                  onClick={() => {
                    setMediaDialogShow(true);
                    setMediaUpdateParams({
                      dialog: {
                        title: "更改课程标题音频",
                        accept: "audio/*",
                        folder: matchFolder["titleMp3"],
                        fileNamePrefix:
                          matchFileNamePrefix["titleMp3"] +
                          (courseInfo as CourseDetailType)?.articleId,
                      },
                      params: {
                        article_id: (courseInfo as CourseDetailType)?.articleId, // 课程ID
                        cate: "base", // 变更模块
                        field: "title_mp3", // 修改字段
                        value: courseInfo?.titleMp3 as string,
                      },
                    });
                    setMediaEditValue(courseInfo?.titleMp3);
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Edit className="h-4 w-4 text-indigo-600" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent
          value="core"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <div className="rounded-lg border p-4 space-y-4">
            <div className="rounded-md border overflow-auto max-h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">ID</TableHead>
                    <TableHead className="whitespace-nowrap">
                      Ariticle_ID
                    </TableHead>
                    <TableHead className="whitespace-nowrap">word</TableHead>
                    <TableHead className="whitespace-nowrap">pos</TableHead>
                    <TableHead className="whitespace-nowrap">
                      example_sentence_en
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      example_sentence_cn
                    </TableHead>
                    <TableHead className="whitespace-nowrap">mp3</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!courseInfo?.coreWord ||
                  courseInfo?.coreWord?.length === 0 ? (
                    <TableRow key="Not found">
                      <TableCell colSpan={12} className="h-24 text-center">
                        没有找到课程记录
                      </TableCell>
                    </TableRow>
                  ) : (
                    courseInfo?.coreWord?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="whitespace-nowrap">
                          {item.id}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.article_id}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.word}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.pos}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.example_sentence_en}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.example_sentence_cn}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {item.example_sentence_mp3}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
        <TabsContent
          value="animation"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <div className="rounded-lg border p-4 space-y-4">
            <div className="flex flex-col gap-10">
              <div className="flex items-center">
                <Label className="inline-block w-36 text-lg font-semibold">
                  动画文件
                </Label>
                <a href="#" className="text-blue-600">
                  {courseInfo?.animation?.video_url}
                </a>
                <Button
                  className="ml-2"
                  onClick={() => {
                    if (courseInfo?.animation?.video_url) {
                      downloadMedia(
                        formatCoverUrl(
                          courseInfo?.animation?.video_url,
                          "video"
                        )
                      );
                    }
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Download className=" h-4 w-4 text-indigo-600 text-red-400" />
                </Button>
                <Button
                  className="ml-2"
                  onClick={() => {
                    setMediaDialogShow(true);
                    setMediaUpdateParams({
                      dialog: {
                        title: "更改动画文件",
                        accept: "video/*",
                        folder: matchFolder["video"],
                        fileNamePrefix:
                          matchFileNamePrefix["video"] +
                          (courseInfo as CourseDetailType)?.articleId,
                      },
                      params: {
                        article_id: (courseInfo as CourseDetailType)?.articleId, // 课程ID
                        cate: "animation", // 变更模块
                        field: "video_url", // 修改字段
                        value: courseInfo?.animation?.video_url as string,
                      },
                    });
                    setMediaEditValue(courseInfo?.animation?.video_url);
                  }}
                  variant="ghost"
                  size="icon"
                >
                  <Edit className="h-4 w-4 text-indigo-600" />
                </Button>
              </div>
              <div className="flex">
                <Label className="inline-block w-36 text-lg font-semibold">
                  字幕文件
                </Label>
                <div className="w-full">
                  <div className="w-full">
                    <Button
                      className=""
                      onClick={() => {
                        const fileName = `${
                          courseInfo?.articleId
                        }-${Date.now()}.json`;
                        if (courseInfo?.animation?.subtitle) {
                          downloadSubtitle(
                            courseInfo?.animation?.subtitle,
                            fileName
                          );
                        }
                      }}
                      variant="ghost"
                      size="icon"
                    >
                      <Download className=" h-4 w-4 text-indigo-600 text-red-400" />
                    </Button>
                    <Button
                      className="ml-2"
                      onClick={() => {
                        setSubtitleDialogShow(true);
                      }}
                      variant="ghost"
                      size="icon"
                    >
                      <Edit className="h-4 w-4 text-indigo-600" />
                    </Button>
                  </div>
                  <div
                    style={{ maxHeight: "calc(100vh - 25rem)" }}
                    className="grid grid-cols-2 grow pt-4 gap-x-12 overflow-y-scroll"
                  >
                    {courseInfo?.animation?.subtitle
                      ?.reduce(
                        (pre: animationSubtitle[], cur: animationSubtitle) => {
                          pre.push(cur);
                          return pre;
                        },
                        []
                      )
                      ?.map((subTitle) => (
                        <Fragment key={subTitle?.id}>
                          <div>
                            <pre className="whitespace-pre-wrap break-words">
                              {subTitle?.en}
                            </pre>
                          </div>
                          <div>
                            <pre className="whitespace-pre-wrap break-words">
                              {subTitle?.zh}
                            </pre>
                          </div>
                        </Fragment>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent
          value="article"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <Tabs
            defaultValue={innerPage}
            className="w-full mx-auto"
            onValueChange={(page: string) =>
              setInnerPage(page as CourseDetailPage)
            }
          >
            <TabsList>
              <TabsTrigger
                value="audio"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
              >
                中英文
              </TabsTrigger>
              <TabsTrigger
                value="analysis"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
              >
                解析词
              </TabsTrigger>
              <TabsTrigger
                value="annotation"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
              >
                注释词
              </TabsTrigger>
              <TabsTrigger
                value="articleAndTest"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
              >
                阅读题
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="audio"
              className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
            >
              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex flex-col gap-10">
                  <div className="flex items-center">
                    <Label className="inline-block w-36 text-lg font-semibold">
                      文章音频
                    </Label>
                    <a href="#" className="text-blue-600">
                      {courseInfo?.audio?.audio_url}
                    </a>
                    <Button
                      className="ml-2"
                      onClick={() => {
                        if (courseInfo?.audio?.audio_url) {
                          downloadMedia(
                            formatCoverUrl(
                              courseInfo?.audio?.audio_url,
                              "articleMp3"
                            )
                          );
                        }
                      }}
                      variant="ghost"
                      size="icon"
                    >
                      <Download className=" h-4 w-4 text-indigo-600 text-red-400" />
                    </Button>
                    <Button
                      className="ml-2"
                      onClick={() => {
                        setMediaDialogShow(true);
                        setMediaUpdateParams({
                          dialog: {
                            title: "更改文章音频",
                            accept: "audio/*",
                            folder: matchFolder["articleMp3"],
                            fileNamePrefix:
                              matchFileNamePrefix["articleMp3"] +
                              (courseInfo as CourseDetailType)?.articleId,
                          },
                          params: {
                            article_id: (courseInfo as CourseDetailType)
                              ?.articleId,
                            cate: "audio",
                            field: "audio_url",
                            value: courseInfo?.audio?.audio_url as string,
                          },
                        });
                        setMediaEditValue(courseInfo?.audio?.audio_url);
                      }}
                      variant="ghost"
                      size="icon"
                    >
                      <Edit className="h-4 w-4 text-indigo-600" />
                    </Button>
                  </div>
                  <div className="flex items-center">
                    <Label className="inline-block w-36 text-lg font-semibold">
                      字幕文件
                    </Label>
                    <a href="#" className="text-blue-600">
                      {courseInfo?.titleMp3}
                    </a>
                    <Button
                      className=""
                      onClick={() => {
                        const fileName = `${
                          courseInfo?.articleId
                        }-${Date.now()}.json`;
                        if (courseInfo?.audio?.subtitle) {
                          downloadSubtitle(
                            courseInfo?.audio?.subtitle,
                            fileName
                          );
                        }
                      }}
                      variant="ghost"
                      size="icon"
                    >
                      <Download className=" h-4 w-4 text-indigo-600 text-red-400" />
                    </Button>
                    <Button
                      className="ml-2"
                      onClick={() => setSubtitleDialogShow(true)}
                      variant="ghost"
                      size="icon"
                    >
                      <Edit className="h-4 w-4 text-indigo-600" />
                    </Button>
                  </div>
                  <div className="flex">
                    <Label className="inline-block w-36 text-lg font-semibold">
                      文章内容
                    </Label>
                    <div
                      className="grid grid-cols-2 grow overflow-y-scroll"
                      style={{ maxHeight: "calc(100vh - 31rem)" }}
                    >
                      {courseInfo?.audio?.subtitle
                        ?.reduce((pre: audioSubtitle[], cur: audioSubtitle) => {
                          pre.push(cur);
                          if (cur.lineBreak == 2) {
                            pre.push({
                              en: " ",
                              zh: " ",
                            } as audioSubtitle);
                          }
                          return pre;
                        }, [])
                        .map((subTitle) => (
                          <Fragment key={subTitle?.id}>
                            <div>
                              <pre className="whitespace-pre-wrap break-words">
                                {subTitle?.en}
                              </pre>
                            </div>
                            <div>
                              <pre className="whitespace-pre-wrap break-words">
                                {subTitle?.zh}
                              </pre>
                            </div>
                          </Fragment>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent
              value="analysis"
              className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
            >
              <div className="rounded-lg border p-4 space-y-4">
                <div className="rounded-md border overflow-auto max-h-[60vh]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">ID</TableHead>
                        <TableHead className="whitespace-nowrap">
                          单词英文
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          词性
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          单词中文
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          解析内容
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          操作
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!courseInfo?.analysisTerms ||
                      courseInfo?.analysisTerms?.length === 0 ? (
                        <TableRow key="Not found">
                          <TableCell colSpan={12} className="h-24 text-center">
                            没有找到解析词记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        courseInfo?.analysisTerms?.map((item) => (
                          <TableRow key={item.article_id}>
                            <TableCell className="whitespace-nowrap">
                              {item.article_id}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.term}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.pos}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.definition_cn}
                            </TableCell>
                            <TableCell>
                              <MarkdownPreview markdown={item.explanation} />
                            </TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setChosenAnalysisTerms(item);
                                        setAnalysisTerms(item?.explanation);
                                        setAnalysisTermsDialogShow(true);
                                      }}
                                    >
                                      <Edit className="h-4 w-4 text-indigo-600" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>修改解析内容</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
            <TabsContent
              value="annotation"
              className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
            >
              <div className="rounded-lg border p-4 space-y-4">
                <div className="rounded-md border overflow-auto max-h-[60vh]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">ID</TableHead>
                        <TableHead className="whitespace-nowrap">
                          Article_ID
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          word
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          释义
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          操作
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!courseInfo?.annotationTerms ||
                      courseInfo?.annotationTerms?.length === 0 ? (
                        <TableRow key="Not found">
                          <TableCell colSpan={12} className="h-24 text-center">
                            没有找到注释词记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        courseInfo?.annotationTerms?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="whitespace-nowrap">
                              {item.id}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.article_id}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.word}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {item.definition_cn}
                            </TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setChosenAnnotationTerms(item);
                                        setAnnotationTerms(item?.definition_cn);
                                        setAnnotationTermsDialogShow(true);
                                      }}
                                    >
                                      <Edit className="h-4 w-4 text-indigo-600" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>修改注释内容</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
            <TabsContent
              value="articleAndTest"
              className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
            >
              <div className="rounded-lg border p-4 space-y-4">
                <div className="rounded-md border overflow-auto max-h-[60vh] p-5">
                  {courseInfo?.articleQuestions?.map((question, index) => {
                    return (
                      <div className="mb-8" key={question?.id}>
                        <div className="flex items-center">
                          <Label className="inline-block text-xl font-semibold">
                            第{index + 1}题：
                          </Label>
                          <span className="inline-block text-xl">
                            {question?.question_stem_en}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2"
                            onClick={() => {
                              setTextEditDialogShow(true);
                              setTextEditParams({
                                dialog: {
                                  title: "修改题目标题",
                                },
                                params: {
                                  article_id: question?.article_id,
                                  cate: "articleAndTest",
                                  field: "question_stem_en",
                                  sub_id: question?.id,
                                  value: question?.question_stem_en,
                                },
                              });
                              setTextEditValue(question?.question_stem_en);
                            }}
                          >
                            <Edit className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                        <div className="flex items-center">
                          <Label className="inline-block text-sm">
                            题干音频：
                          </Label>
                          <a href="#" className="text-blue-600 text-sm">
                            {question?.question_stem_en_play}
                          </a>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2"
                            onClick={() => {
                              setMediaDialogShow(true);
                              setMediaUpdateParams({
                                dialog: {
                                  title: "更改题干音频",
                                  accept: "audio/*",
                                  folder: matchFolder["articleTestMp3"],
                                  fileNamePrefix:
                                    matchFileNamePrefix["articleTestMp3"] +
                                    (courseInfo as CourseDetailType)?.articleId,
                                },
                                params: {
                                  article_id: courseInfo?.articleId,
                                  cate: "articleAndTest",
                                  field: "question_stem_en_play",
                                  sub_id: question?.id,
                                  value: question?.question_stem_en_play,
                                },
                              });
                              setMediaEditValue(
                                question?.question_stem_en_play
                              );
                            }}
                          >
                            <Edit className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                        <div className="flex items-center">
                          <span className="inline-block text-base">
                            {question?.question_stem_zh}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2"
                            onClick={() => {
                              setTextEditDialogShow(true);
                              setTextEditParams({
                                dialog: {
                                  title: "修改中文题目标题",
                                },
                                params: {
                                  article_id: question?.article_id,
                                  cate: "articleAndTest",
                                  field: "question_stem_zh",
                                  sub_id: question?.id,
                                  value: question?.question_stem_zh,
                                },
                              });
                              setTextEditValue(question?.question_stem_zh);
                            }}
                          >
                            <Edit className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                        <div>
                          {question?.options?.map((option) => {
                            return (
                              <div
                                className="flex items-center"
                                key={option?.id}
                              >
                                <div className="font-semibold pr-2">
                                  {option?.option}.
                                </div>
                                <div>{option?.title}</div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="ml-2"
                                  onClick={() => {
                                    setTextEditDialogShow(true);
                                    setTextEditParams({
                                      dialog: {
                                        title: "修改选项内容",
                                      },
                                      params: {
                                        article_id: question?.article_id,
                                        cate: "articleAndTest",
                                        sub_id: question?.id,
                                        field: "options",
                                        option_id: option?.id,
                                        option_field: "title",
                                        value: option?.title,
                                      },
                                    });
                                    setTextEditValue(option?.title);
                                  }}
                                >
                                  <Edit className="h-4 w-4 text-indigo-600" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                        <div>
                          <Label className="inline-block font-semibold text-base">
                            答案：
                          </Label>
                          <span className="inline-block font-semibold text-base">
                            {question?.answer}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2"
                            onClick={() => {
                              setChosenQuestion(
                                question as articleQuestionsType
                              );
                              setTextEditDialogShow(true);
                              setTextEditParams({
                                dialog: {
                                  title: "修改问题答案",
                                  type: "radio",
                                  radioOption: "option",
                                  radioOptionContent: "title",
                                },
                                params: {
                                  article_id: question?.article_id,
                                  cate: "articleAndTest",
                                  field: "answer",
                                  sub_id: question?.id,
                                  value: question?.answer,
                                },
                              });
                              setTextEditValue(question?.answer);
                            }}
                          >
                            <Edit className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                        <div>
                          {question?.explanation}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-2"
                            onClick={() => {
                              setTextEditDialogShow(true);
                              setTextEditParams({
                                dialog: {
                                  title: "修改问题解析",
                                  type: "textarea",
                                  rows: 5,
                                },
                                params: {
                                  article_id: question?.article_id,
                                  cate: "articleAndTest",
                                  field: "explanation",
                                  sub_id: question?.id,
                                  value: question?.explanation,
                                },
                              });
                              setTextEditValue(question?.explanation);
                            }}
                          >
                            <Edit className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent
          value="skills"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <Tabs defaultValue="知识点1" className="w-full mx-auto">
            <TabsList>
              {courseInfo?.extensionSkills?.map((_, index) => (
                <TabsTrigger
                  value={`知识点${index + 1}`}
                  key={index}
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
                >
                  知识点{index + 1}
                </TabsTrigger>
              ))}
            </TabsList>
            {courseInfo?.extensionSkills?.map((skill, index) => (
              <TabsContent
                value={`知识点${index + 1}`}
                key={index + 1}
                className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
              >
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="rounded-md border overflow-auto max-h-[60vh] p-5">
                    <div className="flex items-center">
                      <div className="text-xl font-semibold">
                        知识点{index + 1}：{skill?.strategy_name}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-2"
                        onClick={() => {
                          setTextEditDialogShow(true);
                          setTextEditParams({
                            dialog: {
                              title: "修改标题",
                            },
                            params: {
                              article_id: skill?.article_id,
                              cate: "skills",
                              field: "strategy_name",
                              sub_id: skill?.id,
                              value: skill?.strategy_name,
                            },
                          });
                          setTextEditValue(skill?.strategy_name);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <Label className="text-sm text-gray-400">
                          策略释义
                        </Label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setTextEditParams({
                              dialog: {
                                title: "修改策略释义",
                                type: "textarea",
                              },
                              params: {
                                article_id: skill?.article_id,
                                cate: "skills",
                                field: "strategy_introduce",
                                sub_id: skill?.id,
                                value: skill?.strategy_introduce,
                              },
                            });
                            setTextEditDialogShow(true);
                            setTextEditValue(skill?.strategy_introduce);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                      <div>{skill?.strategy_introduce}</div>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <Label className="text-sm text-gray-400">
                          技巧讲解
                        </Label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setTextEditParams({
                              dialog: {
                                title: "修改技巧讲解",
                                type: "markdown",
                                rows: 10,
                              },
                              params: {
                                article_id: skill?.article_id,
                                cate: "skills",
                                field: "content",
                                sub_id: skill?.id,
                                value: skill?.content,
                              },
                            });
                            setTextEditDialogShow(true);
                            setTextEditValue(skill?.content);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                      <MarkdownPreview markdown={skill.content} />
                    </div>
                    <div className="mt-2">
                      {skill?.question?.map((question, questionIndex) => (
                        <div key={question?.id}>
                          <div className="flex items-center">
                            <div className="text-lg font-semibold">
                              题目{index + 1}.{questionIndex + 1}：
                              {question?.topic}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="ml-2"
                              onClick={() => {
                                setTextEditDialogShow(true);
                                setTextEditParams({
                                  dialog: {
                                    title: "修改标题",
                                  },
                                  params: {
                                    article_id: skill?.article_id,
                                    cate: "skills",
                                    field: "question",
                                    sub_id: skill?.id,
                                    question_field: "topic",
                                    question_id: question?.id,
                                    value: question?.topic,
                                  },
                                });
                                setTextEditValue(question?.topic);
                              }}
                            >
                              <Edit className="h-4 w-4 text-indigo-600" />
                            </Button>
                          </div>
                          <div className="flex items-center">
                            <div className="text-base">
                              题型：{question?.type}
                            </div>
                          </div>
                          <div>
                            {["多选", "单选", "排序"].includes(
                              question?.type
                            ) &&
                              question?.options?.map((option) => (
                                <div
                                  className="flex items-center"
                                  key={option?.id}
                                >
                                  <Label className="text-base pr-2">
                                    {option?.option}.
                                  </Label>
                                  <span>{option?.title}</span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="ml-2"
                                    onClick={() => {
                                      setTextEditDialogShow(true);
                                      setTextEditParams({
                                        dialog: {
                                          title: "修改标题",
                                        },
                                        params: {
                                          article_id: skill?.article_id,
                                          cate: "skills",
                                          sub_id: skill?.id,
                                          field: "question",
                                          question_id: question?.id,
                                          question_field: "options",
                                          option_id: option?.id,
                                          option_field: "title",
                                          value: option?.title,
                                        },
                                      });
                                      setTextEditValue(option?.title);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 text-indigo-600" />
                                  </Button>
                                  {["多选", "单选"].includes(question?.type) &&
                                    !(
                                      question?.options?.filter(
                                        (option) => option?.answer == "True"
                                      )?.length == 1 && option?.answer == "True"
                                    ) && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setTextEditDialogShow(true);
                                          setTextEditParams({
                                            dialog: {
                                              title: "删除选项",
                                              type: "confirm",
                                              question: "确定删除该选项吗？",
                                              width: "max-w-sm",
                                            },
                                            params: {
                                              article_id: skill?.article_id,
                                              cate: "skills",
                                              sub_id: skill?.id,
                                              field: "question",
                                              question_id: question?.id,
                                              question_field: "options",
                                              option_id: option?.id,
                                              operation: "delete",
                                              value: "",
                                            },
                                          });
                                          setTextEditValue("delete");
                                          // updateTextDialog()
                                        }}
                                        className="flex items-center ml-2"
                                      >
                                        <CircleMinus className="h-4 w-4 text-indigo-600 text-red-400" />
                                      </Button>
                                    )}
                                </div>
                              ))}
                            {["多选", "单选"].includes(question?.type) && (
                              <Button
                                variant="secondary"
                                size="icon"
                                onClick={() => {
                                  setAddOptionDialogShow(true);
                                  setChosenQuestion(question);
                                }}
                                className="flex items-center w-40"
                              >
                                <CirclePlus className="h-4 w-4 text-indigo-600" />
                              </Button>
                            )}
                            {question?.type === "连线" && (
                              <div className="flex gap-40">
                                <div>
                                  {question?.options?.map(
                                    (option, optionIndex) => {
                                      return (
                                        <div
                                          className="flex gap-40"
                                          key={option?.id}
                                        >
                                          <div className="flex min-w-40">
                                            <div className="flex items-center">
                                              <Label className="text-base pr-2 font-semibold">
                                                {optionIndex + 1}.
                                              </Label>
                                              {option?.title}
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="ml-2"
                                              onClick={() => {
                                                setTextEditDialogShow(true);
                                                setTextEditParams({
                                                  dialog: {
                                                    title: "修改选项内容",
                                                  },
                                                  params: {
                                                    article_id:
                                                      skill?.article_id,
                                                    cate: "skills",
                                                    sub_id: skill?.id,
                                                    field: "question",
                                                    question_id: question?.id,
                                                    question_field: "options",
                                                    option_id: option?.id,
                                                    option_field: "title",
                                                    value: option?.title,
                                                  },
                                                });
                                                setTextEditValue(option?.title);
                                              }}
                                            >
                                              <Edit className="h-4 w-4 text-indigo-600" />
                                            </Button>
                                          </div>
                                          <div className="flex">
                                            <div className="flex items-center">
                                              <Label className="text-base pr-2 font-semibold">
                                                {option?.option}.
                                              </Label>
                                              {option?.answer}
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="ml-2"
                                              onClick={() => {
                                                setTextEditDialogShow(true);
                                                setTextEditParams({
                                                  dialog: {
                                                    title: "修改答案内容",
                                                  },
                                                  params: {
                                                    article_id:
                                                      skill?.article_id,
                                                    cate: "skills",
                                                    sub_id: skill?.id,
                                                    field: "question",
                                                    question_id: question?.id,
                                                    question_field: "options",
                                                    option_id: option?.id,
                                                    option_field: "answer",
                                                    value: option?.answer,
                                                  },
                                                });
                                                setTextEditValue(
                                                  option?.answer
                                                );
                                              }}
                                            >
                                              <Edit className="h-4 w-4 text-indigo-600" />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center">
                            <div>
                              答案：
                              {["多选", "单选"].includes(question?.type) &&
                                question?.options
                                  ?.filter((option) => option?.answer == "True")
                                  ?.map((option) => option?.option)
                                  ?.join(" ")}
                              {question?.type === "排序" &&
                                [...(question?.options || [])]
                                  ?.sort(
                                    (a, b) =>
                                      a.answer.charCodeAt(0) -
                                      b.answer.charCodeAt(0)
                                  )
                                  .map((option) => option.option)
                                  ?.join("-")}
                              {question?.type === "连线" &&
                                question?.options
                                  .map(
                                    (option, optionIndex) =>
                                      `${optionIndex + 1}-${option?.option}`
                                  )
                                  ?.join(", ")}
                            </div>

                            {["多选", "单选"].includes(question?.type) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="ml-2"
                                onClick={() => {
                                  setChosenQuestion(question);
                                  setTextEditDialogShow(true);
                                  setTextEditParams({
                                    dialog: {
                                      title: "修改答案",
                                      type: "checkbox",
                                    },
                                    params: {
                                      article_id: skill?.article_id,
                                      cate: "skills",
                                      sub_id: skill?.id,
                                      field: "question",
                                      question_id: question?.id,
                                      question_field: "options",
                                      option_field: "answer",
                                      value: "",
                                    },
                                  });
                                  setAnswerCheckbox(
                                    question?.options
                                      ?.filter(
                                        (option) => option?.answer == "True"
                                      )
                                      ?.map((option) => option?.id)
                                  );
                                }}
                              >
                                <Edit className="h-4 w-4 text-indigo-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
        <TabsContent
          value="grammerAndTest"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <Tabs defaultValue="语法点1" className="w-full mx-auto">
            <TabsList>
              {courseInfo?.learnGrammar?.map((_, index) => (
                <TabsTrigger
                  value={`语法点${index + 1}`}
                  key={index}
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
                >
                  语法点{index + 1}
                </TabsTrigger>
              ))}
            </TabsList>
            {courseInfo?.learnGrammar?.map((grammar, index) => (
              <TabsContent
                value={`语法点${index + 1}`}
                key={index}
                className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
              >
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="rounded-md border overflow-auto max-h-[60vh] p-5">
                    <div className="flex items-center">
                      <div className="text-xl font-semibold">
                        语法点{index + 1}：{grammar?.knowledge}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-2"
                        onClick={() => {
                          setTextEditDialogShow(true);
                          setTextEditParams({
                            dialog: {
                              title: "修改标题",
                            },
                            params: {
                              article_id: grammar?.article_id,
                              cate: "grammerAndTest",
                              field: "knowledge",
                              sub_id: grammar?.id,
                              // sub_field: "knowledge",
                              value: grammar?.knowledge,
                            },
                          });
                          setTextEditValue(grammar?.knowledge);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    <div className="mt-4 mb-4">
                      <div className="flex items-center">
                        <Label className="text-sm text-gray-400">
                          知识点简介
                        </Label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setTextEditDialogShow(true);
                            setTextEditParams({
                              dialog: {
                                title: "修改知识点简介",
                                type: "textarea",
                              },
                              params: {
                                article_id: grammar?.article_id,
                                cate: "grammerAndTest",
                                field: "Introduction",
                                sub_id: grammar?.id,
                                // sub_field: "knowledge",
                                value: grammar?.Introduction,
                              },
                            });
                            setTextEditValue(grammar?.Introduction);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                      <div>{grammar?.Introduction}</div>
                    </div>
                    <div className="mt-4 mb-4">
                      <div className="flex items-center">
                        <Label className="text-sm text-gray-400">
                          知识点解析
                        </Label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setTextEditDialogShow(true);
                            setTextEditParams({
                              dialog: {
                                title: "修改知识点解析",
                                type: "markdown",
                              },
                              params: {
                                article_id: grammar?.article_id,
                                cate: "grammerAndTest",
                                field: "analysis",
                                sub_id: grammar?.id,
                                value: grammar?.analysis,
                              },
                            });
                            setTextEditValue(grammar?.analysis);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                      <MarkdownPreview markdown={grammar?.analysis} />
                    </div>
                    <div>
                      {grammar?.question?.map(
                        (question: grammarQuestionType, questionIndex) => (
                          <div key={question?.question_id}>
                            <div className="flex items-center">
                              <div className="text-lg font-semibold">
                                题目{index + 1}.{questionIndex + 1}：
                                {question?.question_en}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 ml-2"
                                onClick={() => {
                                  setTextEditDialogShow(true);
                                  setTextEditParams({
                                    dialog: {
                                      title: "修改题干",
                                    },
                                    params: {
                                      article_id: grammar?.article_id,
                                      cate: "grammerAndTest",
                                      sub_id: grammar?.id,
                                      field: "question",
                                      question_field: "question_en",
                                      question_id: question?.question_id,
                                      value: question?.question_en,
                                    },
                                  });
                                  setTextEditValue(question?.question_en);
                                }}
                              >
                                <Edit className="h-4 w-4 text-indigo-600" />
                              </Button>
                            </div>
                            <div className="flex items-center">
                              <div>
                                <Label className="text-base text-gray-500">
                                  题干翻译：
                                </Label>
                                {question?.question_cn}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 ml-2"
                                onClick={() => {
                                  setTextEditDialogShow(true);
                                  setTextEditParams({
                                    dialog: {
                                      title: "修改题干翻译",
                                    },
                                    params: {
                                      article_id: grammar?.article_id,
                                      cate: "grammerAndTest",
                                      field: "question",
                                      question_field: "question_cn",
                                      sub_id: grammar?.id,
                                      question_id: question?.question_id,
                                      value: question?.question_cn,
                                    },
                                  });
                                  setTextEditValue(question?.question_cn);
                                }}
                              >
                                <Edit className="h-4 w-4 text-indigo-600" />
                              </Button>
                            </div>
                            <div>
                              {question?.options?.map((option) => (
                                <div
                                  className="flex items-center"
                                  key={option?.id}
                                >
                                  <div className="text-base pr-2">
                                    {option?.key}.
                                  </div>
                                  <div>{option?.value}</div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 ml-2"
                                    onClick={() => {
                                      setTextEditDialogShow(true);
                                      setTextEditParams({
                                        dialog: {
                                          title: "修改选项内容",
                                        },
                                        params: {
                                          article_id: grammar?.article_id,
                                          cate: "grammerAndTest",
                                          field: "question",
                                          sub_id: grammar?.id,
                                          question_field: "options",
                                          question_id: question?.question_id,
                                          option_field: "value",
                                          option_id: option?.id,
                                          value: option?.value,
                                        },
                                      });
                                      setTextEditValue(option?.value);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 text-indigo-600" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center">
                              <div>答案：</div>
                              <div>{question?.answer}</div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 ml-2"
                                onClick={() => {
                                  setChosenQuestion(question);
                                  setTextEditDialogShow(true);
                                  setTextEditParams({
                                    dialog: {
                                      title: "修改问题答案",
                                      type: "radio",
                                      radioOption: "key",
                                      radioOptionContent: "value",
                                    },
                                    params: {
                                      article_id: grammar?.article_id,
                                      cate: "grammerAndTest",
                                      field: "question",
                                      question_field: "answer",
                                      sub_id: grammar?.id,
                                      question_id: question?.question_id,
                                      value: question?.answer,
                                    },
                                  });
                                  setTextEditValue(question?.answer);
                                }}
                              >
                                <Edit className="h-4 w-4 text-indigo-600" />
                              </Button>
                            </div>
                            <div className="flex items-center">
                              <div>{question?.analysis}</div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 ml-2"
                                onClick={() => {
                                  setTextEditDialogShow(true);
                                  setTextEditParams({
                                    dialog: {
                                      title: "修改问题解析",
                                      type: "textarea",
                                    },
                                    params: {
                                      article_id: grammar?.article_id,
                                      cate: "grammerAndTest",
                                      field: "question",
                                      question_field: "analysis",
                                      sub_id: grammar?.id,
                                      question_id: question?.question_id,
                                      value: question?.analysis,
                                    },
                                  });
                                  setTextEditValue(question?.analysis);
                                }}
                              >
                                <Edit className="h-4 w-4 text-indigo-600" />
                              </Button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
        <TabsContent
          value="speaking"
          className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
        >
          <Tabs defaultValue="基于文本" className="w-full mx-auto">
            <TabsList>
              {courseInfo?.practiceSpeaking?.map((speaking, index) => (
                <TabsTrigger
                  value={`${speaking.type}`}
                  key={index}
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm rounded-md py-2 px-4 transition-all duration-200"
                >
                  {speaking.type}口语题
                </TabsTrigger>
              ))}
            </TabsList>
            {courseInfo?.practiceSpeaking?.map((speaking, index) => (
              <TabsContent
                value={`${speaking.type}`}
                key={index}
                className="animate-in fade-in-50 duration-300 space-y-2 mt-3"
              >
                <div className="rounded-lg border p-4 space-y-4">
                  <div className="rounded-md border overflow-auto max-h-[60vh] p-5">
                    <div className="flex items-center">
                      <Label className="inline-block w-24 text-lg font-semibold text-">
                        口语问题
                      </Label>
                      <div>{speaking?.question_en}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 ml-2"
                        onClick={() => {
                          setTextEditDialogShow(true);
                          setTextEditParams({
                            dialog: {
                              title: "修改问题",
                            },
                            params: {
                              article_id: speaking?.article_id,
                              cate: "speaking",
                              field: "question_en",
                              sub_id: speaking?.id,
                              // sub_field: "knowledge",
                              value: speaking?.question_en,
                            },
                          });
                          setTextEditValue(speaking?.question_en);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    <div className="flex items-center">
                      <Label className="inline-block w-24 text-lg font-semibold text-">
                        题干音频1
                      </Label>
                      <div>
                        <span className="text-blue-500 cursor-pointer">
                          {speaking?.question_mp3}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 ml-2"
                        onClick={() => {
                          setMediaDialogShow(true);
                          setMediaUpdateParams({
                            dialog: {
                              title: "更改题干音频",
                              accept: "audio/*",
                              folder: matchFolder["speakTestTitleMp3"],
                              fileNamePrefix:
                                matchFileNamePrefix["speakTestTitleMp3"] +
                                (courseInfo as CourseDetailType)?.articleId,
                            },
                            params: {
                              article_id: courseInfo?.articleId,
                              cate: "speaking",
                              field: "question_mp3",
                              sub_id: speaking?.id,
                              value: speaking?.question_mp3,
                            },
                          });
                          setMediaEditValue(speaking?.question_mp3);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    <div className="flex items-center">
                      <Label className="inline-block w-24 text-lg font-semibold text-">
                        问题翻译
                      </Label>
                      <div>{speaking?.question_zh}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 ml-2"
                        onClick={() => {
                          setTextEditDialogShow(true);
                          setTextEditParams({
                            dialog: {
                              title: "修改问题翻译",
                            },
                            params: {
                              article_id: speaking?.article_id,
                              cate: "speaking",
                              field: "question_zh",
                              sub_id: speaking?.id,
                              // sub_field: "knowledge",
                              value: speaking?.question_zh,
                            },
                          });
                          setTextEditValue(speaking?.question_zh);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    {speaking?.pic_name && (
                      <div className="flex items-center">
                        <div className="w-20"></div>
                        <img
                          src={formatCoverUrl(
                            speaking?.pic_name,
                            "speakTestPic"
                          )}
                          alt={speaking?.question_id}
                          className="w-auto h-auto rounded-lg object-cover cursor-pointer"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setMediaDialogShow(true);
                            setMediaUpdateParams({
                              dialog: {
                                title: "更改提示图片",
                                accept: "image/*",
                                folder: matchFolder["speakTestPic"],
                                fileNamePrefix:
                                  matchFileNamePrefix["speakTestPic"] +
                                  (courseInfo as CourseDetailType)?.articleId,
                              },
                              params: {
                                article_id: courseInfo?.articleId,
                                cate: "speaking",
                                field: "pic_name",
                                sub_id: speaking?.id,
                                value: speaking?.pic_name,
                              },
                            });
                            setMediaEditValue(speaking?.pic_name);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                    )}
                    {speaking?.type !== "基于文本" && (
                      <div className="flex items-center">
                        <Label className="inline-block w-24 text-lg font-semibold text-">
                          问题提示
                        </Label>
                        <pre>{speaking?.sentence_frame}</pre>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 ml-2"
                          onClick={() => {
                            setTextEditDialogShow(true);
                            setTextEditParams({
                              dialog: {
                                title: "修改问题提示",
                              },
                              params: {
                                article_id: speaking?.article_id,
                                cate: "speaking",
                                field: "sentence_frame",
                                sub_id: speaking?.id,
                                value: speaking?.sentence_frame,
                              },
                            });
                            setTextEditValue(speaking?.sentence_frame);
                          }}
                        >
                          <Edit className="h-4 w-4 text-indigo-600" />
                        </Button>
                      </div>
                    )}
                    <div></div>
                    <div></div>
                    <div className="flex items-center">
                      <Label className="inline-block w-24 text-lg font-semibold text-">
                        参考答案
                      </Label>
                      <div>{speaking?.sample_answer}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 ml-2"
                        onClick={() => {
                          setTextEditDialogShow(true);
                          setTextEditParams({
                            dialog: {
                              title: "修改参考答案",
                            },
                            params: {
                              article_id: speaking?.article_id,
                              cate: "speaking",
                              field: "sample_answer",
                              sub_id: speaking?.id,
                              value: speaking?.sample_answer,
                            },
                          });
                          setTextEditValue(speaking?.sample_answer);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                    <div className="flex items-center">
                      <Label className="inline-block w-24 text-lg font-semibold text-">
                        答案音频
                      </Label>
                      <span className="text-blue-500 cursor-pointer">
                        {speaking?.sample_answer_mp3}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 ml-2"
                        onClick={() => {
                          setMediaDialogShow(true);
                          setMediaUpdateParams({
                            dialog: {
                              title: "更改答案音频",
                              accept: "audio/*",
                              folder: matchFolder["speakTestAnswerMp3"],
                              fileNamePrefix:
                                matchFileNamePrefix["speakTestAnswerMp3"] +
                                (courseInfo as CourseDetailType)?.articleId,
                            },
                            params: {
                              article_id: courseInfo?.articleId,
                              cate: "speaking",
                              field: "sample_answer_mp3",
                              sub_id: speaking?.id,
                              value: speaking?.sample_answer_mp3,
                            },
                          });
                          setMediaEditValue(speaking?.sample_answer_mp3);
                        }}
                      >
                        <Edit className="h-4 w-4 text-indigo-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
      </Tabs>
      {/* 更新课程标题 */}
      <Dialog
        open={titleDialogShow}
        onOpenChange={(open) => !open && setTitleDialogShow(false)}
      >
        <DialogContent className="sm:max-w-md bg-white gap-10">
          <DialogHeader>
            <DialogTitle className="text-center">修改课程标题</DialogTitle>
          </DialogHeader>
          <Input
            defaultValue={courseInfo?.title}
            onChange={(e) => {
              setTitleEdit(e.target.value);
            }}
          />
          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={!titleEdit || titleEdit == courseInfo?.title}
              onClick={updateTitle}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 更新字幕文件 */}
      <Dialog
        open={subtitleDialogShow}
        onOpenChange={(open) => {
          if (!open) {
            setSubtitleDialogShow(false);
            setSubtitleFile(undefined);
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl bg-white gap-5">
          <DialogHeader>
            <DialogTitle className="text-center">修改字幕文件</DialogTitle>
          </DialogHeader>
          <Input
            type="file"
            accept="application/json"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              if (!file) return;

              // 读取并解析文件内容
              const reader = new FileReader();
              reader.onload = (event) => {
                try {
                  const content = event.target?.result as string;
                  const parsedData = JSON.parse(content); // 解析JSON
                  setSubtitleFile(parsedData);
                } catch (err) {
                  // 解析失败（如JSON格式错误）
                  console.error("解析失败：", err);
                }
              };

              // 读取文件为文本
              reader.readAsText(file);
              console.log(file);
            }}
          />
          {subtitleFile && (
            <div className="w-full">
              <div className="font-semibold">预览文件</div>
              <pre
                style={{ overflowWrap: "anywhere" }}
                className="max-h-96 w-full text-wrap overflow-y-scroll h-full bg-slate-100 rounded-sm p-4"
              >
                {JSON.stringify(subtitleFile, null, 2)}
              </pre>
            </div>
          )}
          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button disabled={!subtitleFile} onClick={updateSubtitle}>
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 更新解析词内容 */}
      <Dialog
        open={analysisTermsDialogShow}
        onOpenChange={(open) => !open && setAnalysisTermsDialogShow(false)}
      >
        <DialogContent className="sm:max-w-3xl bg-white gap-10">
          <DialogHeader>
            <DialogTitle className="text-center">修改解析词内容</DialogTitle>
          </DialogHeader>
          <MarkdownEditor
            defaultValue={analysisTerms as string}
            onChange={(value: string) => setAnalysisTerms(value)}
          />
          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={
                !analysisTerms ||
                analysisTerms === chosenAnalysisTerms?.explanation
              }
              onClick={updateAnalysisTerms}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 更新注释词内容 */}
      <Dialog
        open={annotationTermsDialogShow}
        onOpenChange={(open) => !open && setAnnotationTermsDialogShow(false)}
      >
        <DialogContent className="sm:max-w-3xl bg-white gap-10">
          <DialogHeader>
            <DialogTitle className="text-center">修改注释词内容</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="ml-2 pb-2 text-gray-400 block">释义:</Label>
            <Input
              defaultValue={annotationTerms}
              onChange={(e) => setAnnotationTerms(e.target.value)}
            />
          </div>

          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={
                !annotationTerms ||
                annotationTerms === chosenAnnotationTerms?.definition_cn
              }
              onClick={updateAnnotationTerms}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 文本弹窗 */}
      <Dialog
        open={textEditDialogShow}
        onOpenChange={(open) => !open && setTextEditDialogShow(false)}
      >
        <DialogContent
          className={`bg-white gap-10 ${
            textEditParams?.dialog?.width
              ? textEditParams?.dialog?.width
              : "sm:max-w-3xl"
          }`}
        >
          <DialogHeader>
            <DialogTitle className="text-center">
              {textEditParams?.dialog?.title}
            </DialogTitle>
          </DialogHeader>
          {textEditParams?.dialog?.type === "textarea" && (
            <Textarea
              defaultValue={textEditValue}
              onChange={(e) => setTextEditValue(e.target.value)}
              rows={textEditParams.dialog.rows || 5}
            />
          )}
          {textEditParams?.dialog?.type === "markdown" && (
            <MarkdownEditor
              defaultValue={textEditValue || ""}
              onChange={(value) => setTextEditValue(value)}
            />
          )}
          {textEditParams?.dialog?.type === "confirm" && (
            <div>{textEditParams?.dialog?.question}</div>
          )}
          {(textEditParams?.dialog?.type === "confirm" ||
            !textEditParams?.dialog?.type) && (
            <Input
              defaultValue={textEditValue}
              onChange={(e) => setTextEditValue(e.target.value)}
            />
          )}
          {textEditParams?.dialog?.type === "checkbox" && (
            <div>
              {(chosenQuestion as chooseQuestionType)?.options?.map(
                (option) => {
                  return (
                    <div className="flex items-center gap-4" key={option?.id}>
                      <input
                        onChange={(e) => {
                          if (answerCheckbox) {
                            if (answerCheckbox.includes(e.target.value)) {
                              setAnswerCheckbox((answer) => {
                                answer = answer?.filter(
                                  (answer) => answer !== e.target.value
                                );
                                return [...(answer || [])];
                              });
                            } else {
                              setAnswerCheckbox((answer) => {
                                answer?.push(e.target.value);
                                return [...(answer || [])];
                              });
                            }
                          } else {
                            if (e.target.checked) {
                              setAnswerCheckbox([e.target.value]);
                            } else {
                              setAnswerCheckbox([]);
                            }
                          }
                        }}
                        defaultChecked={option?.answer == "True" ? true : false}
                        type="checkbox"
                        id={option?.id}
                        name="option"
                        value={option?.id}
                      />
                      <label htmlFor={option?.id}>
                        <span>{option?.option}. </span>
                        {option?.title}
                      </label>
                    </div>
                  );
                }
              )}
            </div>
          )}
          {textEditParams?.dialog?.type === "radio" && (
            <div>
              {chosenQuestion?.options?.map(
                (
                  option: grammarOptionType | articleQuestionOptionType,
                  index
                ) => {
                  const radioOption = textEditParams.dialog.radioOption;
                  const radioOptionContent =
                    textEditParams.dialog.radioOptionContent;
                  if (!radioOption) return;
                  if (!radioOptionContent) return;

                  return (
                    <div className="flex items-center gap-4" key={index}>
                      <input
                        onChange={(e) => {
                          setTextEditValue(e.target.value);
                        }}
                        defaultChecked={
                          (option as grammarOptionType)?.[
                            radioOption as keyof grammarOptionType
                          ] ==
                          (
                            chosenQuestion as
                              | grammarQuestionType
                              | articleQuestionsType
                          )?.answer
                        }
                        type="radio"
                        id={option?.id}
                        name="option"
                        value={
                          (option as grammarOptionType)?.[
                            radioOption as keyof grammarOptionType
                          ]
                        }
                        autoFocus={
                          (option as grammarOptionType)?.[
                            radioOption as keyof grammarOptionType
                          ] ==
                          (
                            chosenQuestion as
                              | grammarQuestionType
                              | articleQuestionsType
                          )?.answer
                        }
                      />
                      <label htmlFor={option?.id}>
                        <span>
                          {
                            (option as grammarOptionType)?.[
                              radioOption as keyof grammarOptionType
                            ]
                          }
                          .{" "}
                        </span>
                        {
                          (option as grammarOptionType)?.[
                            radioOptionContent as keyof grammarOptionType
                          ]
                        }
                      </label>
                    </div>
                  );
                }
              )}
            </div>
          )}

          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            {textEditParams?.dialog?.type !== "checkbox" && (
              <Button
                disabled={
                  !textEditValue ||
                  textEditValue === textEditParams?.params?.value
                }
                onClick={updateTextDialog}
              >
                确认
              </Button>
            )}
            {textEditParams?.dialog?.type === "checkbox" && (
              <Button
                disabled={!answerCheckbox || answerCheckbox?.length == 0}
                onClick={updateCheckboxQuestionAnswer}
              >
                确认
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 添加问题选项 */}
      <Dialog
        open={addOptionDialogShow}
        onOpenChange={(open) => !open && setAddOptionDialogShow(false)}
      >
        <DialogContent className={`sm:max-w-lg bg-white gap-10`}>
          <DialogHeader>
            <DialogTitle className="text-center">新增问题选项</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-8">
            <Label className="flex-shrink-0">
              选项:<span className="text-red-500">*</span>
            </Label>
            <Input
              value={addOption}
              placeholder="请在A-Z中选择一个作为选项输入"
              size={1}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^A-Z]/g, "");
                const limited = filteredValue.slice(0, 1);
                setAddOption(limited);
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="flex-shrink-0">
              选项内容:<span className="text-red-500">*</span>
            </Label>
            <Input
              value={addOptionTitle}
              onChange={(e) => setAddOptionTitle(e.target.value)}
            />
          </div>
          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={!addOption || !addOptionTitle}
              onClick={addNewOption}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 音频修改 */}
      <Dialog
        open={mediaDialogShow}
        onOpenChange={(open) => !open && setMediaDialogShow(false)}
      >
        <DialogContent className={`bg-white gap-10 sm:max-w-lg`}>
          <DialogHeader>
            {/* <DialogTitle className="text-center">{textEditParams?.dialog?.title}</DialogTitle> */}
            <DialogTitle className="text-center">
              {mediaUpdateParams?.dialog?.title}
            </DialogTitle>
          </DialogHeader>
          <OSSUpload
            value={mediaEditValue ? [mediaEditValue] : []}
            // value={courseTitleAudio?[courseTitleAudio]:[]}
            accept={mediaUpdateParams?.dialog?.accept}
            maxCount={1}
            folder={mediaUpdateParams?.dialog?.folder}
            // fileNamePrefix={courseInfo?.articleId || ""}
            fileNamePrefix={mediaUpdateParams?.dialog?.fileNamePrefix}
            onChange={(fileUrls) => {
              if (fileUrls.length == 0) {
                // setCourseTitleAudio(undefined)
                setMediaEditValue(undefined);
                return;
              }
              const fileUrl = fileUrls[0];
              if (!fileUrl) {
                // setCourseTitleAudio(undefined)
                setMediaEditValue(undefined);
                return;
              }
              const fileName = fileUrl.split("/").reverse()[0];
              // setCourseTitleAudio(fileName)
              setMediaEditValue(fileName);
              return;
            }}
          />
          <DialogFooter className="flex justify-evenly">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              disabled={
                !mediaEditValue ||
                mediaEditValue === mediaUpdateParams?.params?.value
              }
              // disabled={!textEditValue || textEditValue === textEditParams?.params?.value}
              onClick={updateMediaDialog}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
